import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { clsx } from 'clsx';
import toast from 'react-hot-toast';
import {
  Building2,
  Plug,
  AlertTriangle,
  TrendingUp,
  Check,
  ArrowRight,
  Bot,
  Plus,
  Loader2,
  ExternalLink,
} from 'lucide-react';
import { useAuth, usePermissions } from '../../contexts/AuthContext';
import WorkspacePill from '../../components/layout/WorkspacePill';
import { AI_APP_META } from '../../config/aiApps';
import {
  homeApi,
  type HomeAiConnection,
  type HomeBusiness,
  type HomePulseSignal,
} from '../../services/home-api';
import { orgApi } from '../../services/org-api';
import { handleApiError } from '../../services/api';

const BTN_PRIMARY =
  'inline-flex items-center justify-center gap-2 px-4 py-2 rounded-lg text-sm font-medium bg-primary text-primary-foreground hover:bg-primary/90 transition-colors';
const BTN_OUTLINE =
  'inline-flex items-center justify-center gap-2 px-4 py-2 rounded-lg text-sm font-medium border border-input bg-card text-foreground hover:bg-accent transition-colors';
const BTN_OUTLINE_SM =
  'inline-flex items-center justify-center gap-1.5 px-3 py-1.5 rounded-lg text-[13px] font-medium border border-input bg-card text-foreground hover:bg-accent transition-colors flex-none';
/** High-contrast CTA for use on `bg-brand-wash`, where the brand button would vanish. */
const BTN_ON_WASH =
  'inline-flex items-center justify-center gap-2 px-[19px] py-2.5 rounded-lg text-[15px] font-semibold btn-on-wash transition-colors';

const HEALTH_DOT: Record<string, string> = {
  healthy: 'bg-success',
  warning: 'bg-warn',
  error: 'bg-destructive',
  disconnected: 'bg-muted-foreground',
};
const dotClass = (status: string): string => HEALTH_DOT[status] ?? 'bg-muted-foreground';

const initials = (name: string): string =>
  name
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((w) => w[0]!.toUpperCase())
    .join('') || '?';

const abbr = (sourceType: string): string => sourceType.replace(/[^a-z0-9]/gi, '').slice(0, 2).toUpperCase();

/** "just now" / "5m ago" / "3h ago" / "2d ago". */
const ago = (iso: string): string => {
  const m = Math.floor((Date.now() - new Date(iso).getTime()) / 60000);
  if (m < 1) return 'just now';
  if (m < 60) return `${m}m ago`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}h ago`;
  return `${Math.floor(h / 24)}d ago`;
};

const relTime = (iso: string | null): string => (iso ? `synced ${ago(iso)}` : 'not synced yet');

const shortDate = (iso: string): string =>
  new Date(iso).toLocaleDateString(undefined, { day: 'numeric', month: 'short' });

const daysSince = (iso: string): number =>
  Math.floor((Date.now() - new Date(iso).getTime()) / 86_400_000);

/** No calls in this many days and an app is reported as quiet rather than active. */
const QUIET_AFTER_DAYS = 14;

const HomeRollup: React.FC = () => {
  const navigate = useNavigate();
  const { workspace } = useAuth();
  const { isAdmin } = usePermissions();

  const { data, isLoading, isError, refetch } = useQuery({
    queryKey: ['homeOverview', workspace?.id],
    queryFn: homeApi.getOverview,
  });

  const businesses = data?.businesses ?? [];
  const single = businesses.length <= 1;
  const showPulse = businesses.some((b) => Array.isArray(b.pulse));

  // "See more details" / "Fix" / "Reconnect" — entering a business is plain
  // navigation with a filter, not a context switch: no /auth/switch, no cache
  // clear. The Data sources page reads `?org=` into its business filter.
  const openBusiness = (orgId: string): void => {
    navigate(`/sources?org=${encodeURIComponent(orgId)}`);
  };

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loader2 className="w-6 h-6 animate-spin text-muted-foreground" />
      </div>
    );
  }

  if (isError) {
    return (
      <div className="max-w-5xl mx-auto px-6 py-16 text-center">
        <p className="text-sm text-muted-foreground">Couldn’t load your home right now.</p>
        <button className={clsx(BTN_OUTLINE, 'mt-4')} onClick={() => refetch()}>
          Try again
        </button>
      </div>
    );
  }

  const totals = data?.totals ?? { businesses: 0, sources: 0, attention: 0 };

  return (
    <div className="max-w-6xl mx-auto px-6 py-7">
      {/* Workspace selector */}
      <div className="flex justify-end">
        <WorkspacePill />
      </div>

      {/* KPI strip — only meaningful with more than one business */}
      {!single && (
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mt-6">
          <KpiTile icon={Building2} label="Businesses" value={totals.businesses} to="/businesses" />
          <KpiTile icon={Plug} label="Connected sources" value={totals.sources} to="/sources" />
          <KpiTile
            icon={AlertTriangle}
            label="Needs attention"
            value={totals.attention}
            accent={totals.attention > 0 ? 'text-warn' : 'text-foreground'}
            to="/sources"
          />
        </div>
      )}

      {/* Ask across all businesses — connect-or-report */}
      <AskAiCard
        connections={data?.aiConnections ?? []}
        calls30Days={data?.mcpCalls.last30Days ?? 0}
        onManage={() => navigate('/settings/external-ai-access')}
        onSetup={() => navigate('/connect-ai')}
      />

      {/* Businesses */}
      <div className="flex items-center justify-between mt-7 mb-3">
        <h3 className="text-[15px] font-semibold text-foreground">
          {single ? 'Your business' : 'Your businesses'}
        </h3>
      </div>

      {businesses.length === 0 ? (
        <div className="rounded-lg border border-border bg-card shadow-card p-8 text-center">
          <p className="text-sm text-muted-foreground">You don’t have access to any connected sources yet.</p>
          <button className={clsx(BTN_PRIMARY, 'mt-4')} onClick={() => navigate('/sources')}>
            Connect a source
          </button>
        </div>
      ) : (
        <div className="flex flex-col gap-3.5">
          {businesses.map((b) => (
            <BusinessRow key={b.org.id} business={b} showPulse={showPulse} onOpen={openBusiness} />
          ))}
        </div>
      )}

      {/* Add a business — owner/admin only */}
      {isAdmin && <AddBusinessRow onCreated={openBusiness} />}
    </div>
  );
};

/* ---------------------------------- KPI ---------------------------------- */

const KpiTile: React.FC<{
  icon: React.ComponentType<{ className?: string }>;
  label: string;
  value: number;
  accent?: string;
  to: string;
}> = ({ icon: Icon, label, value, accent, to }) => (
  <Link
    to={to}
    className="group block rounded-lg border border-border bg-card shadow-card p-4 transition-colors hover:border-input hover:bg-accent"
  >
    <div className="flex items-center gap-2 text-[11px] font-bold uppercase tracking-wide text-muted-foreground">
      <Icon className="w-4 h-4" /> {label}
    </div>
    <div className={clsx('text-2xl font-semibold mt-2 group-hover:underline', accent ?? 'text-foreground')}>
      {value}
    </div>
  </Link>
);

/* --------------------------------- Ask AI -------------------------------- */

const AskAiCard: React.FC<{
  connections: HomeAiConnection[];
  calls30Days: number;
  onManage: () => void;
  onSetup: () => void;
}> = ({ connections, calls30Days, onManage, onSetup }) =>
  connections.length === 0 ? (
    <ConnectAiBanner onSetup={onSetup} />
  ) : (
    <ConnectedAiCard
      connections={connections}
      calls30Days={calls30Days}
      onManage={onManage}
      onSetup={onSetup}
    />
  );

/**
 * Disconnected state. The one place in the product that wears `bg-brand-wash` —
 * it has to win attention against everything below it on the home page. Kept
 * deliberately short: the specifics of what has been collected belong in the
 * setup guide, not on a card people skim.
 */
const ConnectAiBanner: React.FC<{ onSetup: () => void }> = ({ onSetup }) => (
  <div className="rounded-lg border border-border shadow-card bg-brand-wash text-on-wash mt-4 p-5">
    <div className="flex flex-wrap items-center gap-6">
      <div className="flex-1 min-w-[250px]">
        <div className="text-[11px] font-extrabold uppercase tracking-wide text-on-wash-muted">
          External AI access
        </div>
        <h2 className="text-[26px] leading-tight font-extrabold tracking-tight mt-2">
          Ask your business anything.
        </h2>
        <p className="text-[14.5px] text-on-wash-muted mt-1.5">
          In ChatGPT or Claude. Takes two minutes.
        </p>
      </div>

      <div className="flex flex-none items-center gap-3">
        <span className="wash-tile w-11 h-11" aria-hidden="true">
          <img src={AI_APP_META.chatgpt.logo!} alt="" className="w-[26px] h-[26px] rounded" />
        </span>
        <span className="wash-tile w-11 h-11" aria-hidden="true">
          <img src={AI_APP_META.claude.logo!} alt="" className="w-[26px] h-[26px] rounded" />
        </span>
        <button className={clsx(BTN_ON_WASH, 'ml-1.5')} onClick={onSetup}>
          Connect your AI <ArrowRight className="w-4 h-4" />
        </button>
      </div>
    </div>
  </div>
);

/** Connected state — one row per linked app, plus a permanent way to add another. */
const ConnectedAiCard: React.FC<{
  connections: HomeAiConnection[];
  calls30Days: number;
  onManage: () => void;
  onSetup: () => void;
}> = ({ connections, calls30Days, onManage, onSetup }) => {
  // One scale across every row: per-row auto-scaling would make a quiet app's
  // sparkline as tall as a busy one's.
  const peak = Math.max(1, ...connections.flatMap((c) => c.daily));

  return (
    <div className="rounded-lg border border-border bg-card shadow-card mt-4">
      <div className="flex items-center gap-2.5 px-5 pt-4 pb-3 text-[11px] font-bold uppercase tracking-wide text-muted-foreground">
        <span className="inline-flex items-center gap-1.5 rounded-full bg-success/15 text-success px-2.5 py-0.5 text-xs font-medium normal-case tracking-normal">
          <span className="w-1.5 h-1.5 rounded-full bg-success" /> AI connected
        </span>
        External AI access
        <span className="ml-auto text-xs font-normal normal-case tracking-normal">
          <span className="font-semibold text-foreground tabular-nums">
            {calls30Days.toLocaleString()}
          </span>{' '}
          requests · last 30 days
        </span>
      </div>

      {connections.map((c) => (
        <AiConnectionRow key={c.app} connection={c} peak={peak} onManage={onManage} />
      ))}

      <button
        className="w-full flex items-center gap-2.5 border-t border-dashed border-border px-5 py-3 text-[13.5px] font-medium text-muted-foreground hover:text-foreground hover:bg-accent/50 transition-colors"
        onClick={onSetup}
      >
        <span className="w-6 h-6 rounded-md bg-muted grid place-items-center">
          <Plus className="w-4 h-4" />
        </span>
        Connect another AI
        <span className="ml-auto text-[11.5px] hidden sm:inline">
          Claude Code, ChatGPT Desktop, any MCP client
        </span>
      </button>
    </div>
  );
};

const AiConnectionRow: React.FC<{
  connection: HomeAiConnection;
  peak: number;
  onManage: () => void;
}> = ({ connection, peak, onManage }) => {
  const { app, connectedAt, lastUsedAt, calls30Days, daily } = connection;
  const meta = AI_APP_META[app];
  const quiet = !lastUsedAt || daysSince(lastUsedAt) >= QUIET_AFTER_DAYS;

  return (
    <div className="flex items-center gap-3.5 border-t border-border px-5 py-3">
      {meta.logo ? (
        <img src={meta.logo} alt="" className="w-[34px] h-[34px] rounded-lg flex-none" />
      ) : (
        <span className="w-[34px] h-[34px] rounded-lg bg-muted grid place-items-center flex-none">
          <Bot className="w-[18px] h-[18px] text-muted-foreground" />
        </span>
      )}

      <div className="min-w-0 flex-1">
        <div className="flex items-center gap-2">
          {meta.openUrl ? (
            <a
              href={meta.openUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-1 text-sm font-semibold text-foreground hover:underline"
            >
              {meta.label} <ExternalLink className="w-3 h-3 text-muted-foreground" />
            </a>
          ) : (
            <span className="text-sm font-semibold text-foreground">{meta.label}</span>
          )}
          <span
            className={clsx(
              'inline-flex items-center gap-1.5 rounded-full px-2 py-px text-[11px] font-medium',
              quiet ? 'bg-muted text-muted-foreground' : 'bg-success/15 text-success',
            )}
          >
            <span
              className={clsx('w-1.5 h-1.5 rounded-full', quiet ? 'bg-muted-foreground' : 'bg-success')}
            />
            {quiet ? 'Quiet' : 'Active'}
          </span>
        </div>
        <div className="mt-px text-[11.5px] text-muted-foreground truncate">
          {connectedAt && `Connected ${shortDate(connectedAt)} · `}
          {lastUsedAt ? `asked ${ago(lastUsedAt)}` : 'not used yet'}
        </div>
      </div>

      <Sparkline data={daily} peak={peak} />

      <div className="w-16 text-right flex-none">
        <div className="text-[17px] font-bold text-foreground tabular-nums leading-tight">
          {calls30Days.toLocaleString()}
        </div>
        <div className="text-[11.5px] text-muted-foreground">requests</div>
      </div>

      <button className={BTN_OUTLINE_SM} onClick={onManage}>
        Manage
      </button>
    </div>
  );
};

/** Daily call counts. Decorative — the adjacent total carries the same story. */
const Sparkline: React.FC<{ data: number[]; peak: number }> = ({ data, peak }) => (
  <div className="hidden sm:flex w-[110px] h-[22px] flex-none items-end gap-[2px]" aria-hidden="true">
    {data.map((calls, i) => (
      <span
        key={i}
        className={clsx(
          'flex-1 min-w-px rounded-t-sm',
          calls === 0
            ? 'bg-muted-foreground/20'
            : i >= data.length - 7
              ? 'bg-primary'
              : 'bg-primary/30',
        )}
        // Floor at 12% so a single call is still visible against the zero rule.
        style={{ height: calls === 0 ? 2 : `${Math.max(12, Math.round((calls / peak) * 100))}%` }}
      />
    ))}
  </div>
);

/* ------------------------------ Business row ----------------------------- */

const BusinessRow: React.FC<{
  business: HomeBusiness;
  showPulse: boolean;
  onOpen: (orgId: string) => void;
}> = ({ business, showPulse, onOpen }) => {
  const { org, sources, attention, pulse } = business;
  return (
    <div
      className={clsx(
        'rounded-lg border border-border bg-card shadow-card overflow-hidden grid grid-cols-1',
        showPulse ? 'md:grid-cols-[1.3fr_1fr_1.2fr]' : 'md:grid-cols-[1.4fr_1.2fr]',
      )}
    >
      {/* Business */}
      <div className="p-5">
        <div className="flex items-center gap-3">
          <span className="w-9 h-9 rounded-lg bg-primary/15 text-primary grid place-items-center text-sm font-bold">
            {initials(org.name)}
          </span>
          <div className="min-w-0">
            <div className="text-[15px] font-semibold text-foreground truncate">{org.name}</div>
            <div className="text-xs text-muted-foreground mt-0.5">
              {sources.length} source{sources.length === 1 ? '' : 's'}
            </div>
          </div>
        </div>

        <div className="flex gap-1.5 mt-3.5 flex-wrap">
          {sources.map((s) => (
            <span
              key={s.id}
              title={`${s.name} · ${s.healthStatus} · ${relTime(s.lastSync)}`}
              className="relative w-7 h-7 rounded-md bg-muted border border-border grid place-items-center text-[9px] font-bold text-muted-foreground"
            >
              {abbr(s.sourceType)}
              {s.healthStatus !== 'healthy' && (
                <span
                  className={clsx(
                    'absolute -top-1 -right-1 w-2.5 h-2.5 rounded-full border-2 border-card',
                    dotClass(s.healthStatus),
                  )}
                />
              )}
            </span>
          ))}
        </div>

        <button
          className="mt-4 inline-flex items-center gap-1.5 text-[13px] font-semibold text-primary hover:underline"
          onClick={() => onOpen(org.id)}
        >
          See more details… <ArrowRight className="w-3.5 h-3.5" />
        </button>
      </div>

      {/* Pulse */}
      {showPulse && (
        <div className="p-5 border-t md:border-t-0 md:border-l border-border">
          <div className="text-[10px] font-bold uppercase tracking-wider text-muted-foreground mb-3">Pulse</div>
          {pulse && pulse.length > 0 ? (
            <div className="space-y-3">
              {pulse.map((sig, i) => (
                <PulseRow key={i} signal={sig} />
              ))}
            </div>
          ) : (
            <div className="text-[13px] text-muted-foreground">No new signals</div>
          )}
        </div>
      )}

      {/* Needs attention */}
      <div className="p-5 border-t md:border-t-0 md:border-l border-border">
        <div className="text-[10px] font-bold uppercase tracking-wider text-muted-foreground mb-3">
          Needs attention
        </div>
        {attention.length === 0 ? (
          <div className="flex items-center gap-2 text-[13px] text-muted-foreground">
            <Check className="w-4 h-4 text-success" /> All clear
          </div>
        ) : (
          <div className="space-y-3">
            {attention.map((a) => (
              <div key={a.sourceId} className="flex gap-3 items-start">
                <span className="w-7 h-7 rounded-lg bg-warn/15 text-warn grid place-items-center flex-none">
                  <AlertTriangle className="w-4 h-4" />
                </span>
                <div className="min-w-0">
                  <div className="text-[13.5px] font-semibold text-foreground">{a.sourceName}</div>
                  <div className="text-xs text-muted-foreground mt-0.5 leading-snug">{a.message}</div>
                  <button className={clsx(BTN_OUTLINE_SM, 'mt-2.5')} onClick={() => onOpen(org.id)}>
                    {a.kind === 'error' ? 'Reconnect' : 'Fix'}
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

const PulseRow: React.FC<{ signal: HomePulseSignal }> = ({ signal }) => (
  <div className="flex gap-2.5 items-start">
    <span
      className={clsx(
        'w-7 h-7 rounded-lg grid place-items-center flex-none',
        signal.severity === 'warn' ? 'bg-warn/15 text-warn' : 'bg-info/15 text-info',
      )}
    >
      {signal.severity === 'warn' ? (
        <AlertTriangle className="w-4 h-4" />
      ) : (
        <TrendingUp className="w-4 h-4" />
      )}
    </span>
    <div className="min-w-0">
      <div className="text-[13px] font-semibold text-foreground leading-tight">{signal.title}</div>
      {(signal.source || signal.detail) && (
        <div className="text-[11.5px] text-muted-foreground mt-0.5">{signal.detail ?? signal.source}</div>
      )}
    </div>
  </div>
);

/* ---------------------------- Add a business ----------------------------- */

const AddBusinessRow: React.FC<{ onCreated: (orgId: string) => void }> = ({ onCreated }) => {
  const [editing, setEditing] = useState(false);
  const [name, setName] = useState('');
  const [saving, setSaving] = useState(false);
  const queryClient = useQueryClient();

  const create = async (): Promise<void> => {
    const trimmed = name.trim();
    if (!trimmed) return;
    setSaving(true);
    try {
      const org = await orgApi.createOrganization(trimmed);
      await queryClient.invalidateQueries({ queryKey: ['homeOverview'] });
      toast.success('Business created — connect its first source.');
      // Enter the new business so the user can connect a source (it appears in
      // the roll-up once a source is connected).
      onCreated(org.id);
    } catch (err) {
      toast.error(handleApiError(err));
    } finally {
      setSaving(false);
    }
  };

  if (!editing) {
    return (
      <button
        className="mt-3.5 w-full flex items-center gap-2.5 rounded-lg border border-dashed border-border bg-card px-5 py-4 text-sm font-medium text-muted-foreground hover:border-input hover:text-foreground transition-colors"
        onClick={() => setEditing(true)}
      >
        <span className="w-6 h-6 rounded-md bg-muted grid place-items-center">
          <Plus className="w-4 h-4" />
        </span>
        Add a business
      </button>
    );
  }

  return (
    <div className="mt-3.5 flex items-center gap-2 rounded-lg border border-border bg-card shadow-card px-4 py-3">
      <input
        autoFocus
        value={name}
        onChange={(e) => setName(e.target.value)}
        onKeyDown={(e) => e.key === 'Enter' && create()}
        placeholder="Business name"
        className="flex-1 bg-transparent text-sm text-foreground placeholder:text-muted-foreground focus:outline-none"
        disabled={saving}
      />
      <button className={BTN_PRIMARY} onClick={create} disabled={saving || !name.trim()}>
        {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Create'}
      </button>
      <button
        className={BTN_OUTLINE_SM}
        onClick={() => {
          setEditing(false);
          setName('');
        }}
        disabled={saving}
      >
        Cancel
      </button>
    </div>
  );
};

export default HomeRollup;
